<script setup>
import { computed, inject, onMounted, ref, watch } from "vue";
import { Review, Topic } from "@/services";
import { useMainStore } from "@/stores/main.js";
import { mapPeriodToDateRange } from "@/utils";
import MHeader from "@/components/m/MHeader.vue";
import MPeriodScope from "@/components/m/MPeriodScope.vue";
import MPullScroll from "@/components/m/MPullScroll.vue";
import MSheet from "@/components/m/MSheet.vue";
import MIcon from "@/components/m/MIcon.vue";
import MStars from "@/components/m/MStars.vue";
import MSpark from "@/components/m/MSpark.vue";
import BtSourceMark from "@/components/bt/BtSourceMark.vue";
import BtAreaTrend from "@/components/bt/BtAreaTrend.vue";

const ctx = inject("mctx");
const mainStore = useMainStore();
const intent = ctx.intent.value || {};

const SC = { pos: "var(--pos)", neg: "var(--neg)", neu: "var(--neu)" };
const SL = { pos: "Positive", neg: "Negative", neu: "Neutral" };
const classOf = (v) => (Number(v) > 0 ? "pos" : Number(v) < 0 ? "neg" : "neu");

const topics = ref([]);
const loading = ref(false);
const topicsError = ref(false);
const filter = ref(null); // 'pos' | 'neg' | 'neu' | null
const search = ref("");
const selLabel = ref(intent.topic || null);

// Trend (detail sheet)
const trend = ref(null);
const trendLoading = ref(false);
const trendError = ref(false);

// Reviews (detail sheet) — paginated / load-more
const REVIEWS_PER_PAGE = 12;
const reviews = ref([]);
const reviewPage = ref(1);
const reviewTotal = ref(0);
const reviewsLoading = ref(false);
const loadingMoreReviews = ref(false);
const reviewsError = ref(false);

// Track / create topic
const formOpen = ref(false);
const form = ref({ label: "", sentiment: "positive" });
const formError = ref("");
const saving = ref(false);

const aggregation = computed(() =>
  (mainStore.selectedAggregation || "monthly").toLowerCase(),
);
// A label carries mentions of every polarity — match the filter on whether it has any
// mentions of that polarity rather than on its blended average, which hid the
// minority side of a mixed topic.
const POLARITY_FIELD = {
  pos: "positive_mentions",
  neu: "neutral_mentions",
  neg: "negative_mentions",
};
const POLARITY_WORD = { pos: "positive", neu: "neutral", neg: "negative" };

const polarityWord = computed(() => POLARITY_WORD[filter.value] || "");
const mentionsOf = (t) => {
  const field = POLARITY_FIELD[filter.value];
  return Number((field ? t?.[field] : t?.mentions) || 0);
};
const toneOf = (t) => SC[filter.value || classOf(t?.sentiment)];

const list = computed(() => {
  let items = topics.value;
  const field = POLARITY_FIELD[filter.value];
  if (field)
    items = items
      .filter((t) => Number(t[field] || 0) > 0)
      .sort((a, b) => Number(b[field] || 0) - Number(a[field] || 0));
  const query = search.value.trim().toLowerCase();
  if (query)
    items = items.filter((t) =>
      String(t.label || "")
        .toLowerCase()
        .includes(query),
    );
  return items;
});
const sel = computed(
  () => topics.value.find((t) => t.label === selLabel.value) || null,
);
const good = (t) => Number(t.sentiment) >= 0 === Number(t.change_30d || 0) >= 0;

// Related topics: same sentiment sign, excluding the selected one.
const relatedTopics = computed(() => {
  const t = sel.value;
  if (!t) return [];
  const sign = Math.sign(Number(t.sentiment));
  return topics.value
    .filter(
      (o) => o.label !== t.label && Math.sign(Number(o.sentiment)) === sign,
    )
    .slice(0, 4);
});

const hasMoreReviews = computed(() => reviews.value.length < reviewTotal.value);

const trendChart = computed(() => {
  const t = trend.value;
  if (!t) return { labels: [], volumes: [], sentiments: [] };
  return {
    labels: t.dates || [],
    volumes: t.series?.find((s) => s.name === "Mentions")?.data || [],
    sentiments: t.series?.find((s) => s.name === "Sentiment")?.data || [],
  };
});

const tiles = computed(() => {
  const t = sel.value;
  if (!t) return [];
  const change = t.change_30d;
  const g = good(t);
  return [
    {
      label: "Mentions",
      value: Number(t.mentions || 0).toLocaleString(),
      sub: "all time",
    },
    {
      label: "Share",
      value:
        trend.value?.share_of_voice != null
          ? trend.value.share_of_voice + "%"
          : "-",
      sub: "of voice",
      accent: "var(--brand-strong)",
    },
    {
      label: "Rating",
      value:
        trend.value?.avg_rating != null
          ? Number(trend.value.avg_rating).toFixed(1) + "★"
          : "-",
      sub: "when cited",
      accent: trend.value?.avg_rating >= 3.5 ? "var(--pos)" : "var(--neg)",
    },
    {
      label: "30d",
      value:
        change != null
          ? (change >= 0 ? "+" : "−") + Math.abs(Math.round(change)) + "%"
          : "-",
      sub: change == null ? "—" : g ? "better" : "worse",
      accent: change == null ? "var(--text)" : g ? "var(--pos)" : "var(--neg)",
    },
  ];
});

function dateRange() {
  const { startDate, endDate } = mapPeriodToDateRange(
    mainStore.selectedPeriod,
    mainStore.customRange,
  );
  return [startDate.toISOString(), endDate.toISOString()];
}

async function fetchTopics() {
  if (!mainStore.activeCompanyId) return;
  loading.value = true;
  topicsError.value = false;
  try {
    const [start, end] = dateRange();
    const response = await Review.getTopicsOverview(
      mainStore.activeCompanyId,
      aggregation.value,
      start,
      end,
    );
    topics.value = response.items || [];
    if (
      selLabel.value &&
      topics.value.some((t) => t.label === selLabel.value)
    ) {
      fetchDetail();
    }
  } catch (e) {
    console.error("Error fetching topics:", e);
    topicsError.value = true;
    topics.value = [];
  } finally {
    loading.value = false;
  }
}

async function fetchTrend() {
  const t = sel.value;
  const companyId = mainStore.activeCompanyId;
  if (!t || !companyId) {
    trend.value = null;
    return;
  }
  const [start, end] = dateRange();
  trendLoading.value = true;
  trendError.value = false;
  try {
    trend.value =
      (await Review.getTopicTrend(
        companyId,
        t.label,
        aggregation.value,
        start,
        end,
      )) || null;
  } catch (e) {
    console.error("Error fetching topic trend:", e);
    trend.value = null;
    trendError.value = true;
  } finally {
    trendLoading.value = false;
  }
}

async function fetchReviews(append = false) {
  const t = sel.value;
  const companyId = mainStore.activeCompanyId;
  if (!t || !companyId) {
    reviews.value = [];
    reviewTotal.value = 0;
    return;
  }
  const location = mainStore.selectedLocation;
  const eid = location?.location_id || location?.id;
  const [start, end] = dateRange();
  if (append) loadingMoreReviews.value = true;
  else reviewsLoading.value = true;
  reviewsError.value = false;
  try {
    const response = await Review.getReviewsByCompany(
      companyId,
      eid === "ALL" ? null : eid,
      start,
      end,
      reviewPage.value,
      REVIEWS_PER_PAGE,
      null,
      null,
      t.label,
      null,
    );
    const items = response?.items || [];
    reviews.value = append ? [...reviews.value, ...items] : items;
    reviewTotal.value = response?.total || (append ? reviewTotal.value : 0);
  } catch (e) {
    console.error("Error fetching topic reviews:", e);
    reviewsError.value = true;
    if (!append) {
      reviews.value = [];
      reviewTotal.value = 0;
    }
  } finally {
    if (append) loadingMoreReviews.value = false;
    else reviewsLoading.value = false;
  }
}

function loadMoreReviews() {
  if (loadingMoreReviews.value || !hasMoreReviews.value) return;
  reviewPage.value += 1;
  fetchReviews(true);
}

function fetchDetail() {
  reviewPage.value = 1;
  reviews.value = [];
  reviewTotal.value = 0;
  trend.value = null;
  fetchTrend();
  fetchReviews(false);
}

function openTopic(label) {
  selLabel.value = label;
  fetchDetail();
}

function fmtDate(v) {
  if (!v) return "";
  try {
    return new Date(v).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function openCreate() {
  form.value = { label: "", sentiment: "positive" };
  formError.value = "";
  formOpen.value = true;
}

async function saveTopic() {
  const label = form.value.label.trim();
  if (!label) {
    formError.value = "Topic label is required";
    return;
  }
  if (!mainStore.selectedCompany?.company_id) {
    formError.value = "No company selected";
    return;
  }
  saving.value = true;
  formError.value = "";
  try {
    const sentiment = form.value.sentiment === "negative" ? -1 : 1;
    await Topic.create(mainStore.selectedCompany.company_id, label, sentiment);
    formOpen.value = false;
    await fetchTopics();
  } catch (e) {
    console.error("Error creating topic:", e);
    formError.value = "Could not create topic. Please try again.";
  } finally {
    saving.value = false;
  }
}

watch(() => mainStore.dateFilterKey, fetchTopics);
onMounted(fetchTopics);
</script>

<template>
  <div class="btm-app">
    <MHeader title="Topics" sub="How every topic trends over time" />
    <MPeriodScope />
    <div class="btm-rail" style="padding-bottom: 10px">
      <button
        v-for="[label, value] in [
          ['All', null],
          ['Positive', 'pos'],
          ['Negative', 'neg'],
          ['Neutral', 'neu'],
        ]"
        :key="label"
        class="btm-chip"
        :class="{ on: filter === value }"
        @click="filter = value"
      >
        {{ label }}
      </button>
    </div>

    <MPullScroll :busy="loading" @refresh="fetchTopics">
      <div class="btm-pad" style="padding-bottom: 16px">
        <!-- search + track -->
        <div class="mt-searchrow">
          <div class="mt-search">
            <MIcon name="search" :size="15" class="mt-search-icon" />
            <input
              v-model="search"
              type="search"
              class="mt-input"
              placeholder="Search topics…"
              aria-label="Search topics"
            />
          </div>
          <button
            v-if="!mainStore.isReadOnly"
            class="btm-btn btm-btn-ghost btm-btn-sm mt-track"
            @click="openCreate"
          >
            <MIcon name="plus" :size="15" :sw="2.4" />Track
          </button>
        </div>

        <div class="btm-card">
          <div
            v-for="(tp, i) in list"
            :key="tp.label"
            style="
              display: flex;
              align-items: center;
              gap: 11px;
              padding: 13px 14px;
              cursor: pointer;
            "
            :style="{ borderTop: i ? '1px solid var(--border)' : 'none' }"
            @click="openTopic(tp.label)"
          >
            <span
              style="
                width: 9px;
                height: 9px;
                border-radius: 50%;
                flex: 0 0 auto;
              "
              :style="{ background: toneOf(tp) }"
            />
            <div style="flex: 1; min-width: 0">
              <div
                style="
                  font-weight: 700;
                  font-size: 13.5px;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                "
              >
                {{ tp.label }}
              </div>
              <div class="btm-muted" style="font-size: 11px">
                <template v-if="filter">
                  {{ mentionsOf(tp).toLocaleString() }} {{ polarityWord }} of
                  {{ Number(tp.mentions || 0).toLocaleString() }}
                </template>
                <template v-else>
                  {{ Number(tp.mentions || 0).toLocaleString() }} mentions
                  &middot;
                  <b :style="{ color: 'var(--pos)' }"
                    >{{ tp.positive_mentions || 0 }}+</b
                  >
                  /
                  <b :style="{ color: 'var(--neg)' }"
                    >{{ tp.negative_mentions || 0 }}&minus;</b
                  >
                </template>
              </div>
              <div
                v-if="!filter && Number(tp.mentions || 0) > 0"
                class="bt-split"
              >
                <span
                  v-if="tp.positive_mentions"
                  :style="{
                    flex: tp.positive_mentions,
                    background: 'var(--pos)',
                  }"
                />
                <span
                  v-if="tp.neutral_mentions"
                  :style="{
                    flex: tp.neutral_mentions,
                    background: 'var(--neu)',
                    opacity: 0.5,
                  }"
                />
                <span
                  v-if="tp.negative_mentions"
                  :style="{
                    flex: tp.negative_mentions,
                    background: 'var(--neg)',
                  }"
                />
              </div>
            </div>
            <MSpark
              v-if="tp.series && tp.series.length"
              :data="tp.series"
              :w="44"
              :h="20"
              :color="toneOf(tp)"
              :sw="1.8"
            />
            <span
              v-if="tp.change_30d != null"
              class="btm-delta"
              :class="good(tp) ? 'up' : 'down'"
              ><MIcon
                :name="tp.change_30d >= 0 ? 'arrowupright' : 'arrowdownright'"
                :size="10"
                :sw="2.4"
              />{{ Math.abs(Math.round(tp.change_30d)) }}%</span
            >
          </div>
          <div v-if="loading && !list.length" class="btm-cardpad btm-muted">
            Loading topics…
          </div>
          <div
            v-else-if="topicsError && !list.length"
            class="btm-cardpad btm-muted"
          >
            Couldn't load topics. Pull to refresh.
          </div>
          <div v-else-if="!list.length" class="btm-cardpad btm-muted">
            {{
              search.trim()
                ? "No topics match your search."
                : "No topics in this period."
            }}
          </div>
        </div>
      </div>
    </MPullScroll>

    <MSheet
      :open="selLabel != null"
      label="Topic detail"
      @close="selLabel = null"
    >
      <div v-if="sel">
        <div
          style="
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 14px;
          "
        >
          <span
            style="width: 11px; height: 11px; border-radius: 50%"
            :style="{ background: SC[classOf(sel.sentiment)] }"
          />
          <div style="flex: 1; min-width: 0">
            <div
              style="font-size: 18px; font-weight: 800; letter-spacing: -0.02em"
            >
              {{ sel.label }}
            </div>
            <div class="btm-muted" style="margin-top: 1px">
              {{ Number(sel.mentions || 0).toLocaleString() }} mentions
            </div>
          </div>
          <span class="btm-tag" :class="classOf(sel.sentiment)">{{
            SL[classOf(sel.sentiment)]
          }}</span>
        </div>

        <div style="display: flex; gap: 8px; margin-bottom: 16px">
          <div
            v-for="tile in tiles"
            :key="tile.label"
            style="
              flex: 1;
              background: var(--surface-2);
              border: 1px solid var(--border);
              border-radius: 12px;
              padding: 11px 12px;
            "
          >
            <div class="btm-eyebrow" style="font-size: 9px">
              {{ tile.label }}
            </div>
            <div
              class="btm-num"
              style="font-size: 19px; font-weight: 800; margin: 5px 0 1px"
              :style="{ color: tile.accent || 'var(--text)' }"
            >
              {{ tile.value }}
            </div>
            <div class="btm-muted" style="font-size: 10.5px">
              {{ tile.sub }}
            </div>
          </div>
        </div>

        <div class="btm-card btm-cardpad" style="margin-bottom: 16px">
          <div class="btm-cardhead">
            <div>
              <div class="t" style="font-size: 14px">Mentions & Sentiment</div>
            </div>
            <div style="margin-left: auto; display: flex; gap: 8px">
              <span class="btm-tag brand" style="background: transparent"
                ><span
                  style="
                    width: 7px;
                    height: 7px;
                    border-radius: 50%;
                    background: var(--brand);
                  "
                />Vol</span
              >
              <span class="btm-tag pos" style="background: transparent"
                ><span
                  style="
                    width: 7px;
                    height: 7px;
                    border-radius: 50%;
                    background: var(--pos);
                  "
                />Sent</span
              >
            </div>
          </div>
          <BtAreaTrend
            v-if="trendChart.volumes.length"
            :volumes="trendChart.volumes"
            :sentiments="trendChart.sentiments"
            :labels="trendChart.labels"
            :height="200"
          />
          <div
            v-else-if="trendLoading"
            class="btm-muted"
            style="padding: 30px 0; text-align: center"
          >
            Loading trend…
          </div>
          <div
            v-else-if="trendError"
            class="btm-muted"
            style="padding: 30px 0; text-align: center"
          >
            Couldn't load trend data.
          </div>
          <div
            v-else
            class="btm-muted"
            style="padding: 30px 0; text-align: center"
          >
            No trend data for this period.
          </div>
        </div>

        <!-- related topics -->
        <template v-if="relatedTopics.length">
          <div class="btm-cardhead" style="margin-bottom: 8px">
            <div class="t" style="font-size: 14px">Related topics</div>
            <div class="s" style="margin-left: auto">Similar sentiment</div>
          </div>
          <div class="btm-card btm-cardpad" style="margin-bottom: 16px">
            <button
              v-for="rel in relatedTopics"
              :key="rel.label"
              type="button"
              class="mt-related"
              @click="openTopic(rel.label)"
            >
              <span
                style="
                  width: 7px;
                  height: 7px;
                  border-radius: 50%;
                  flex: 0 0 auto;
                "
                :style="{ background: SC[classOf(rel.sentiment)] }"
              />
              <span
                style="
                  font-weight: 600;
                  font-size: 12.5px;
                  flex: 1;
                  min-width: 0;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                "
                >{{ rel.label }}</span
              >
              <span
                class="btm-num btm-muted"
                style="font-size: 11px; flex: 0 0 auto"
                >{{ Number(rel.mentions || 0).toLocaleString() }}</span
              >
              <MIcon
                name="arrowright"
                :size="14"
                style="color: var(--text-3)"
              />
            </button>
          </div>
        </template>

        <!-- reviews list (paginated) -->
        <div class="btm-cardhead" style="margin-bottom: 8px">
          <div class="t" style="font-size: 14px">Reviews</div>
          <div v-if="reviewTotal" class="s btm-num" style="margin-left: auto">
            {{ reviewTotal.toLocaleString() }}
          </div>
        </div>
        <div class="btm-card btm-cardpad" style="margin-bottom: 16px">
          <div v-for="(r, i) in reviews" :key="r.id || i" class="btm-trow">
            <BtSourceMark :source="r.source" :size="28" />
            <div style="flex: 1; min-width: 0">
              <div
                style="
                  display: flex;
                  align-items: center;
                  gap: 7px;
                  margin-bottom: 2px;
                "
              >
                <MStars :value="r.rating" :size="11" />
                <span style="font-weight: 700; font-size: 12.5px">{{
                  r.title || "Review"
                }}</span>
                <span
                  v-if="r.created"
                  class="btm-muted"
                  style="font-size: 10px; margin-left: auto"
                  >{{ fmtDate(r.created) }}</span
                >
              </div>
              <div
                style="
                  font-size: 12px;
                  color: var(--text-2);
                  line-height: 1.4;
                  display: -webkit-box;
                  -webkit-line-clamp: 2;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                "
              >
                {{ r.content }}
              </div>
            </div>
          </div>

          <div
            v-if="reviewsLoading"
            class="btm-muted"
            style="padding: 8px 0; text-align: center"
          >
            Loading reviews…
          </div>
          <div
            v-else-if="reviewsError && !reviews.length"
            class="btm-muted"
            style="padding: 8px 0; text-align: center"
          >
            Couldn't load reviews.
          </div>
          <div
            v-else-if="!reviews.length"
            class="btm-muted"
            style="padding: 8px 0; text-align: center"
          >
            No reviews mention this topic.
          </div>

          <div
            v-if="loadingMoreReviews"
            class="btm-muted"
            style="padding: 10px 0 2px; text-align: center; font-size: 12px"
          >
            Loading more…
          </div>
          <button
            v-else-if="hasMoreReviews && reviews.length"
            type="button"
            class="btm-btn btm-btn-ghost btm-btn-sm"
            style="margin: 10px auto 2px; display: flex"
            @click="loadMoreReviews"
          >
            Load more
          </button>
        </div>

        <button
          class="btm-btn btm-btn-primary"
          @click="
            mainStore.setSelectedTopic(sel.label);
            ctx.go('Reviews', { topic: sel.label });
          "
        >
          <MIcon name="reviews" :size="16" />View
          {{ Number(sel.mentions || 0).toLocaleString() }} reviews
        </button>
      </div>
    </MSheet>

    <!-- track / create topic -->
    <MSheet :open="formOpen" label="Track topic" @close="formOpen = false">
      <div>
        <div style="font-size: 18px; font-weight: 800; letter-spacing: -0.02em">
          Track topic
        </div>
        <div class="btm-muted" style="margin: 2px 0 16px">
          Add a tracked topic to monitor across reviews.
        </div>

        <label class="btm-eyebrow" style="display: block; margin-bottom: 6px"
          >Topic label</label
        >
        <input
          v-model="form.label"
          class="mt-input mt-input-block"
          placeholder="e.g. Wait times"
          aria-label="Topic label"
          @keyup.enter="saveTopic"
        />

        <label class="btm-eyebrow" style="display: block; margin: 14px 0 6px"
          >Sentiment</label
        >
        <div class="btm-seg" style="margin-bottom: 6px">
          <button
            type="button"
            :class="{ on: form.sentiment === 'positive' }"
            @click="form.sentiment = 'positive'"
          >
            Positive
          </button>
          <button
            type="button"
            :class="{ on: form.sentiment === 'negative' }"
            @click="form.sentiment = 'negative'"
          >
            Negative
          </button>
        </div>

        <div
          v-if="formError"
          style="color: var(--neg); font-size: 12px; margin-top: 10px"
        >
          {{ formError }}
        </div>

        <div style="display: flex; gap: 10px; margin-top: 18px">
          <button
            class="btm-btn btm-btn-ghost"
            style="flex: 1"
            @click="formOpen = false"
          >
            Cancel
          </button>
          <button
            class="btm-btn btm-btn-primary"
            style="flex: 1"
            :disabled="saving"
            @click="saveTopic"
          >
            <MIcon name="check" :size="16" :sw="2.4" />{{
              saving ? "Saving…" : "Save"
            }}
          </button>
        </div>
      </div>
    </MSheet>
  </div>
</template>

<style scoped>
.mt-searchrow {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.mt-search {
  position: relative;
  flex: 1;
  min-width: 0;
}
.mt-search-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-3);
  pointer-events: none;
}
.mt-input {
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  padding: 0 12px 0 34px;
  border: 1px solid var(--border);
  border-radius: var(--r-sm, 10px);
  background: var(--surface);
  color: var(--text);
  font-family: var(--ui);
  font-size: 14px;
  outline: none;
}
.mt-input::placeholder {
  color: var(--text-3);
}
.mt-input:focus {
  border-color: var(--brand-line);
}
.mt-input-block {
  padding-left: 12px;
}
.mt-track {
  flex: 0 0 auto;
}
.mt-related {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 10px;
  border: 0;
  border-radius: var(--r-sm, 10px);
  background: var(--surface-2);
  color: var(--text);
  cursor: pointer;
  text-align: left;
}
.mt-related + .mt-related {
  margin-top: 8px;
}
</style>
